/**
 * SubmissionFilesList — picker des fichiers attachés à une submission.
 *
 * Reçoit la liste de fichiers depuis le détail enrichi
 * (`GET /v1/submissions/:id` retourne `files: SubmissionAttachedFile[]`).
 * Affiche file_name / kind / uploaded_at / status / page_count.
 *
 * Décision PO 9.B : pas d'auto-sélection silencieuse si plusieurs
 * fichiers. Si un seul, on pré-sélectionne. Si 0 ou plusieurs, l'utilisateur
 * doit choisir explicitement.
 *
 * Filtre métier : on n'affiche que les fichiers status='ready' parce que
 * l'API rejette `POST /v1/extractions` sur un file non-ready (409 conflict,
 * cf. routes/extractions.ts ligne ~98). Les autres sont visibles mais
 * disabled, avec un libellé d'état clair.
 */

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { SubmissionAttachedFile } from "../../../lib/queries/submissions";

export type SubmissionFilesListProps = {
  files: SubmissionAttachedFile[];
  selectedFileId: string | null;
  onSelect: (fileId: string | null) => void;
  /**
   * Désactive entièrement le picker (utilisé pendant qu'une extraction
   * tourne — interdire de changer de fichier en cours de job).
   */
  disabled?: boolean;
};

export function SubmissionFilesList(props: SubmissionFilesListProps) {
  const { files, selectedFileId, onSelect, disabled } = props;
  const { t } = useTranslation();

  const readyFiles = useMemo(
    () => files.filter((f) => f.status === "ready"),
    [files],
  );

  // Pré-sélection : 1 seul fichier ready → on le sélectionne, sauf si
  // l'utilisateur en a déjà choisi un (selectedFileId fourni par le parent).
  useEffect(() => {
    if (selectedFileId) return;
    if (readyFiles.length === 1) {
      onSelect(readyFiles[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyFiles.length, selectedFileId]);

  if (files.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-slate-200 p-3 text-sm text-slate-500">
        {t("submissions.detail.files.empty")}
      </p>
    );
  }

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="sr-only">
        {t("submissions.detail.files.legend")}
      </legend>
      {files.map((f) => {
        const isReady = f.status === "ready";
        const isSelected = selectedFileId === f.id;
        return (
          <label
            key={f.id}
            className={
              "flex cursor-pointer items-center gap-3 rounded-md border p-3 transition " +
              (isSelected
                ? "border-brand-500 bg-brand-50"
                : "border-slate-200 hover:bg-slate-50") +
              (!isReady || disabled ? " cursor-not-allowed opacity-60" : "")
            }
          >
            <input
              type="radio"
              name="extraction-file"
              value={f.id}
              checked={isSelected}
              disabled={!isReady || disabled}
              onChange={() => onSelect(f.id)}
              className="mt-0.5"
            />
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded bg-brand-100 text-[10px] font-bold text-brand-800">
              PDF
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-800">
                {f.file_name}
              </p>
              <p className="text-xs text-slate-500">
                {t(`files.kinds.${f.kind}`, { defaultValue: f.kind })} ·{" "}
                {formatBytes(f.size_bytes)}
                {typeof f.page_count === "number"
                  ? ` · ${t("files.pages_count", { count: f.page_count })}`
                  : null}
                {" · "}
                <FileStatusBadge status={f.status} />
                {" · "}
                {formatDate(f.uploaded_at)}
              </p>
            </div>
          </label>
        );
      })}
    </fieldset>
  );
}

function FileStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const className =
    status === "ready"
      ? "text-green-700"
      : status === "errored"
        ? "text-red-700"
        : "text-amber-700";
  return (
    <span className={className}>
      {t(`files.statuses.${status}`, { defaultValue: status })}
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
